import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from '../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const tiers = [
  {
    name: 'Platinum',
    sponsors: ['TechGiant', 'RobotWorld'],
  },
  {
    name: 'Gold',
    sponsors: ['FutureTech', 'InnovateAI', 'SmartSystems'],
  },
  {
    name: 'Silver',
    sponsors: ['EduRobotics', 'AutoMech', 'CodeFuture'],
  },
];

const partners = ['MIT Robotics Lab', 'Stanford AI', 'ETH Zurich', 'Tokyo Tech'];

const Sponsors = () => {
  const sectionRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      // Logos reveal sequentially
      gsap.utils.toArray('.sponsor-logo').forEach((logo, i) => {
        gsap.fromTo(
          logo,
          { opacity: 0, y: 10 },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: 'power2.out',
            delay: i * 0.08,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      id="sponsors"
      ref={sectionRef}
      className="section-padding bg-surface dark:bg-dark-surface relative overflow-hidden"
    >
      <div className="section-container">
        {/* Header */}
        <div className="mb-16 lg:mb-20">
          <span className="tech-label text-accent dark:text-dark-accent mb-6 block">
            SPONSORS / PARTNERS
          </span>
          <h2 className="section-title mb-6">Supported by leaders.</h2>
          <p className="section-desc">
            We're proud to collaborate with leading organizations in technology
            and education.
          </p>
        </div>

        {/* Sponsor tiers — logo system */}
        <div className="space-y-16 mb-20">
          {tiers.map((tier) => (
            <div key={tier.name}>
              <span className="tech-label text-text-muted dark:text-dark-text-muted block text-center mb-8">
                {tier.name} SPONSORS
              </span>
              <div className="flex flex-wrap justify-center gap-6">
                {tier.sponsors.map((name) => (
                  <div
                    key={name}
                    className="sponsor-logo border border-border dark:border-dark-border rounded-card px-8 py-6 flex items-center justify-center min-w-[160px] hover:border-border-strong dark:hover:border-dark-border-strong transition-colors duration-200"
                  >
                    <span className="text-lg font-semibold text-text-muted dark:text-dark-text-muted">
                      {name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Academic Partners */}
        <div className="mb-20">
          <span className="tech-label text-text-muted dark:text-dark-text-muted block text-center mb-8">
            ACADEMIC PARTNERS
          </span>
          <div className="flex flex-wrap justify-center gap-6">
            {partners.map((name) => (
              <div
                key={name}
                className="sponsor-logo border border-border dark:border-dark-border rounded-card px-6 py-4 flex items-center justify-center hover:border-border-strong dark:hover:border-dark-border-strong transition-colors duration-200"
              >
                <span className="text-sm font-medium text-text-muted dark:text-dark-text-muted">
                  {name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-text-primary dark:text-dark-text-primary mb-3">
            Become a Sponsor
          </h3>
          <p className="text-sm text-text-secondary dark:text-dark-text-secondary mb-6 max-w-xl mx-auto">
            Join our prestigious list of sponsors and gain visibility among the
            brightest minds in robotics and technology.
          </p>
          <a href="/sponsorship-package.pdf" download className="btn-primary">
            Download Sponsorship Package
          </a>
        </div>
      </div>
    </section>
  );
};

export default Sponsors;